import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useHover } from "@sundae/react-hooks";
import cx from "classnames";
import { forwardRef, useMemo, useRef } from "react";
import { toolkitTestIds } from "../../../constants";
import Tooltip from "../../organisms/Tooltip";
import NumberI18N from "../NumberI18N";
import Text from "../Text/Text";
import { ESupportedSpecialCurrencies, getSpecialCurrencyDecimals, getSpecialCurrencySymbol, SpecialCurrencySymbols, } from "./currency";
/**
 * The Balance component is used to display asset amounts with various formatting options.
 * It allows for abbreviation, custom currency symbols, and locale-based formatting.
 * It can also show or hide the currency symbol and obfuscate the value for privacy.
 *
 * @component
 * @param {IBalanceProps} props - The props for the Balance component.
 * @param {boolean} [props.abbreviate] - If true, abbreviates large numbers for readability. Default is true.
 * @param {string} [props.containerClassName] - The class name for the Balance container.
 * @param {ESupportedSpecialCurrencies|string} [props.currency] - The currency to use for the balance. One of ESupportedSpecialCurrencies or a custom string. Default is ESupportedSpecialCurrencies.ADA.
 * @param {Intl.NumberFormatOptions} [props.formatOpts] - Additional options for number formatting using Intl.NumberFormat.
 * @param {string} [props.locale] - The locale to use for number formatting. Default is "en".
 * @param {boolean} [props.privacy] - If true, obfuscates the value for privacy. Default is false.
 * @param {boolean} [props.showSymbol] - If true, displays the currency symbol. Default is true.
 * @param {string} [props.testId="balance"] - The test id for the Balance component.
 * @param {number} [props.usdValue] - The value of the balance to display (in USD).
 * @param {number} [props.usdValueTestId] - The test id for the USD Value component.
 * @param {number} [props.usdValueSize] - The size of the USD Value inside the Tooltip.
 * @param {AssetAmount} [props.value] - The value of the balance to display.
 * @param {React.Ref<HTMLElement>} forwardedRef - A ref that can be used to access the underlying HTMLElement of the Balance component.
 * @returns {JSX.Element} A rendered Balance component.
 */
const Balance = ({ abbreviate = true, containerClassName, currency = ESupportedSpecialCurrencies.ADA, formatOpts, locale = "en", privacy = false, showSymbol = true, testId = toolkitTestIds.balance.container, usdValue, usdValueTestId = toolkitTestIds.balance.usdValue, usdValueSize = "xs", value, ...props }, forwardedRef) => {
    const usdValueRef = useRef(null);
    const isUsdValueHovered = useHover(usdValueRef);
    const quotientLength = useMemo(() => value?.value.formatQuotientToLocaleString(locale)?.length || 0, [value, locale]);
    // Format currency by locale.
    const formattedValue = useMemo(() => {
        const parsedOptions = {
            maximumFractionDigits: value?.decimals ?? getSpecialCurrencyDecimals(currency),
            currencyDisplay: "narrowSymbol",
            style: "currency",
            // Use USD as a placeholder if special currency so we can do a replace later.
            currency: SpecialCurrencySymbols.hasOwnProperty(currency)
                ? "USD"
                : currency,
            ...(abbreviate && {
                notation: "compact",
                // Override decimals if large number: force max of 3 digits, otherwise include 2 decimal places
                maximumSignificantDigits: quotientLength > 3 ? 3 : quotientLength + 2,
            }),
            ...formatOpts,
        };
        try {
            const format = new Intl.NumberFormat(locale, parsedOptions);
            const valueNumber = value?.value.toNumber(parsedOptions.maximumFractionDigits) ?? 0;
            const parts = format.formatToParts(valueNumber);
            // Replace placeholder currency sign.
            return parts
                .map(({ type, value }) => {
                if (type === "currency") {
                    return showSymbol ? getSpecialCurrencySymbol(currency, value) : "";
                }
                return value;
            })
                .join("");
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.warn(e.message);
            return null;
        }
    }, [
        value,
        formatOpts,
        abbreviate,
        locale,
        currency,
        showSymbol,
        quotientLength,
    ]);
    const renderedBalance = useMemo(() => {
        if (privacy && formattedValue) {
            return (_jsx(Text, { ...props, tag: "span", testId: testId, ref: forwardedRef, children: "*".repeat(formattedValue.length) }));
        }
        if (usdValue && isUsdValueHovered) {
            return (_jsx(Tooltip, { trigger: _jsx(Text, { ...props, tag: "span", testId: testId, ref: forwardedRef, children: formattedValue }), tooltipLabel: _jsxs(Text, { size: usdValueSize, tag: "span", gradient: false, weight: "normal", variant: "normal", children: ["~", " ", _jsx(NumberI18N, { ...props, className: props.className, formatOpts: {
                                currency: "USD",
                                style: "currency",
                            }, gradient: false, size: usdValueSize, locale: locale, testId: usdValueTestId, value: usdValue, variant: "normal" })] }) }));
        }
        return (_jsx(Text, { ...props, tag: "span", testId: testId, ref: forwardedRef, children: formattedValue }));
    }, [
        privacy,
        formattedValue,
        usdValue,
        props,
        locale,
        testId,
        forwardedRef,
        isUsdValueHovered,
        usdValueTestId,
        usdValueSize,
    ]);
    return (_jsx("div", { className: cx("flex", containerClassName), ref: usdValueRef, children: renderedBalance }));
};
export default forwardRef(Balance);
//# sourceMappingURL=Balance.js.map